import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { 
  Briefcase, Shield, AlertTriangle, FileText, Loader2, ChevronDown, ChevronUp,
  Scale, Search, Phone, Mail, CheckCircle, Clock, ArrowRight, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const priorityStyles = {
  essential: 'bg-red-50 text-red-700 border-red-200',
  recommended: 'bg-amber-50 text-amber-700 border-amber-200',
  optional: 'bg-blue-50 text-blue-700 border-blue-200',
};

const riskStyles = {
  low: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Low Risk' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Medium Risk' },
  high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'High Risk' },
};

const ProfessionalAdvisor = ({ userRole = 'seller' }) => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [documentsText, setDocumentsText] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProps, setLoadingProps] = useState(true);
  const [result, setResult] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  const fetchProperties = useCallback(async () => {
    try {
      const endpoint = userRole === 'seller' ? '/api/properties/my-listings' : '/api/favorites';
      const res = await axios.get(`${API_URL}${endpoint}`);
      setProperties(res.data || []);
    } catch {
      // For buyers without favorites, try all properties
      if (userRole === 'buyer') {
        try {
          const res = await axios.get(`${API_URL}/api/properties`);
          setProperties(res.data || []);
        } catch { setProperties([]); }
      }
    } finally {
      setLoadingProps(false);
    }
  }, [userRole]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const runAnalysis = async () => {
    if (!selectedProperty) {
      toast.error('Please select a property first');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/ai/professional-analysis`, {
        property_id: selectedProperty,
        documents_text: documentsText,
        user_role: userRole,
        additional_notes: additionalNotes,
      });
      setResult(res.data);
      toast.success('Analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const analysis = result?.analysis;
  const risk = riskStyles[analysis?.risk_level] || riskStyles.medium;

  return (
    <div className="space-y-6" data-testid="professional-advisor">
      {/* Liability Banner */}
      <div className="bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="liability-banner">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-amber-800 font-semibold">Important Notice</p>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">
            Shopperden is an introduction service only. All liability for property transactions rests entirely with you.
            You <strong>must</strong> engage independent qualified professionals before proceeding with any property transaction.
            AI analysis is for guidance only and does not constitute professional advice.
          </p>
        </div>
      </div>

      {/* Property Selector + Inputs */}
      <div className="luxury-card p-6 space-y-4">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] flex items-center gap-2">
          <Search className="h-5 w-5 text-[#E07A3A]" />
          AI Professional Recommendation
        </h3>
        <p className="text-slate-500 text-sm">
          Select a property and our AI will analyse the transaction, recommend the professionals you need,
          and flag any concerns — tailored to the property's country and local regulations.
        </p>

        {/* Property Select */}
        <div>
          <label className="text-xs text-slate-600 tracking-wider uppercase font-medium block mb-2">Select Property</label>
          {loadingProps ? (
            <div className="h-12 bg-slate-50 animate-pulse" />
          ) : properties.length > 0 ? (
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full h-12 bg-[#F8FAFC] border border-slate-200 px-4 text-[#1A1A1A] focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
              data-testid="property-select"
            >
              <option value="">Choose a property...</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>
                  {p.title} — {p.city}, {p.country}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-slate-400 text-sm py-3">
              {userRole === 'seller' ? 'No listings found. Create a property listing first.' : 'No properties found. Browse and save properties first.'}
            </p>
          )}
        </div>

        {/* Optional Document Input */}
        <div>
          <button
            onClick={() => setShowDocs(!showDocs)}
            className="flex items-center gap-2 text-sm text-[#E07A3A] font-medium hover:text-[#C4652E] transition-colors"
            data-testid="toggle-docs-btn"
          >
            <FileText className="h-4 w-4" />
            {showDocs ? 'Hide' : 'Add'} Document Details (Optional)
            {showDocs ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          {showDocs && (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-slate-500">
                Paste or describe any documents you have (title deeds, surveys, mortgage offers, contracts, etc.).
                The AI will review them and flag any concerns. <strong>Your choice — no obligation.</strong>
              </p>
              <textarea
                value={documentsText}
                onChange={(e) => setDocumentsText(e.target.value)}
                placeholder="e.g. I have a title deed dated 2019, an EPC rating of C, a mortgage offer from Barclays at 4.5% fixed for 5 years..."
                className="w-full h-32 bg-[#F8FAFC] border border-slate-200 p-4 text-sm text-[#1A1A1A] focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none resize-none"
                data-testid="documents-input"
              />
              <textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Any additional notes or specific concerns? (optional)"
                className="w-full h-20 bg-[#F8FAFC] border border-slate-200 p-4 text-sm text-[#1A1A1A] focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none resize-none"
                data-testid="notes-input"
              />
            </div>
          )}
        </div>

        {/* Analyse Button */}
        <Button
          onClick={runAnalysis}
          disabled={loading || !selectedProperty}
          className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 font-semibold tracking-wider text-sm"
          data-testid="run-analysis-btn"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analysing...</>
          ) : (
            <><Briefcase className="h-4 w-4 mr-2" />Get Professional Recommendations</>
          )}
        </Button>
      </div>

      {/* Results */}
      {result && analysis && (
        <div className="space-y-6" data-testid="analysis-results">
          {/* Summary + Risk */}
          <div className="luxury-card p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Analysis Summary</h3>
              <span className={`px-3 py-1.5 text-xs tracking-wider uppercase font-bold ${risk.bg} ${risk.text} border ${risk.border}`}
                data-testid="risk-level"
              >
                {risk.label}
              </span>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed">{analysis.summary}</p>
          </div>

          {/* Professionals Needed */}
          {analysis.professionals_needed?.length > 0 && (
            <div className="luxury-card p-6">
              <h3 className="font-semibold text-[#1A1A1A] mb-4 text-sm tracking-wider uppercase flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-[#E07A3A]" />
                Professionals You Need
              </h3>
              <div className="space-y-4" data-testid="professionals-list">
                {analysis.professionals_needed.map((prof, i) => (
                  <div key={prof.type} className="border border-slate-100 p-4 hover:border-[#E07A3A]/30 transition-colors">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <Scale className="h-4 w-4 text-[#E07A3A]" />
                        <h4 className="font-semibold text-[#1A1A1A]">{prof.type}</h4>
                      </div>
                      <span className={`px-2 py-0.5 text-[10px] tracking-wider uppercase font-bold border ${priorityStyles[prof.priority] || priorityStyles.recommended}`}>
                        {prof.priority}
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm mb-2">{prof.reason}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                      <div className="bg-slate-50 p-2">
                        <span className="text-slate-400 block mb-0.5">What they do</span>
                        <span className="text-slate-700">{prof.what_they_do}</span>
                      </div>
                      <div className="bg-slate-50 p-2">
                        <span className="text-slate-400 block mb-0.5">Estimated cost</span>
                        <span className="text-slate-700 font-medium">{prof.estimated_cost}</span>
                      </div>
                      <div className="bg-slate-50 p-2">
                        <span className="text-slate-400 block mb-0.5">When to engage</span>
                        <span className="text-slate-700">{prof.when_to_engage}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document Analysis */}
          {analysis.document_analysis && (
            <div className="luxury-card p-6">
              <h3 className="font-semibold text-[#1A1A1A] mb-4 text-sm tracking-wider uppercase flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-[#E07A3A]" />
                Document Review
              </h3>
              {analysis.document_analysis.provided_docs_review && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Documents Provided</p>
                  <p className="text-slate-600 text-sm">{analysis.document_analysis.provided_docs_review}</p>
                </div>
              )}
              {analysis.document_analysis.missing_documents?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Missing Documents</p>
                  <div className="space-y-1">
                    {analysis.document_analysis.missing_documents.map((doc, i) => (
                      <div key={`missing-${doc.slice(0, 20)}`} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-1.5">
                        <Clock className="h-3 w-3 flex-shrink-0" />
                        {doc}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {analysis.document_analysis.red_flags?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Red Flags</p>
                  <div className="space-y-1">
                    {analysis.document_analysis.red_flags.map((flag, i) => (
                      <div key={`flag-${flag.slice(0, 20)}`} className="flex items-center gap-2 text-sm text-red-700 bg-red-50 px-3 py-1.5">
                        <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                        {flag}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Next Steps */}
          {analysis.next_steps?.length > 0 && (
            <div className="luxury-card p-6">
              <h3 className="font-semibold text-[#1A1A1A] mb-4 text-sm tracking-wider uppercase flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-[#E07A3A]" />
                Your Next Steps
              </h3>
              <ol className="space-y-2" data-testid="next-steps">
                {analysis.next_steps.map((step, i) => (
                  <li key={`ns-${step.slice(0, 20)}`} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="w-6 h-6 bg-[#E07A3A] text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Country-Specific Notes */}
          {analysis.country_specific_notes && (
            <div className="luxury-card p-6">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 text-sm tracking-wider uppercase flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#E07A3A]" />
                {result.country === 'UK' ? 'UK' : result.country === 'IN' ? 'India' : result.country === 'CA' ? 'Canada' : result.country} Regulations
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">{analysis.country_specific_notes}</p>
            </div>
          )}

          {/* Platform Professionals */}
          {result.platform_professionals?.length > 0 && (
            <div className="luxury-card p-6">
              <h3 className="font-semibold text-[#1A1A1A] mb-4 text-sm tracking-wider uppercase flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-[#E07A3A]" />
                Shopperden Registered Professionals
              </h3>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="platform-professionals">
                {result.platform_professionals.map((prof, i) => (
                  <div key={prof.name} className="border border-slate-100 p-4 flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-sm">{prof.name.split(' ').map(n => n[0]).join('').slice(0, 2)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1A1A1A] text-sm">{prof.name}</p>
                      <p className="text-xs text-slate-500">{prof.specialization}</p>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {prof.email && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Mail className="h-3 w-3" />{prof.email}
                          </span>
                        )}
                        {prof.phone && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Phone className="h-3 w-3" />{prof.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Liability Footer */}
          <div className="bg-red-50 border border-red-200 p-4 flex items-start gap-3" data-testid="liability-footer">
            <Shield className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-800 font-semibold">Liability Notice</p>
              <p className="text-xs text-red-700 mt-1 leading-relaxed">
                {analysis.liability_notice || 'All decisions and liabilities in relation to this property transaction rest entirely with you. Shopperden provides introduction services only. This AI analysis does not constitute legal, financial, or professional advice. You must engage independent qualified professionals before taking any action.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalAdvisor;
